import React, { useState } from 'react';

import IpdTimelineRow from './IpdTimelineRow';
import OpdTimelineRow from './OpdTimelineRow';
import { TIMELINE_KINDS } from '../../constants/patientCard';
import { ADMISSION_STATUS_LABELS } from '../../constants/admission';
import { formatDateIST, formatDayIST } from '../../utils/formatIST';

const TYPE_CHIP_CLASS = {
  OPD: 'bg-teal-600 text-white',
  IPD: 'bg-blue-600 text-white',
};

const RAIL_CLASS = {
  OPD: 'border-l-teal-500',
  IPD: 'border-l-blue-500',
};

/**
 * TimelineRow — F6, internal to `PatientTimeline`. Owns ONE row's expansion
 * state and hands it to the real detail components (F7 `OpdTimelineRow` /
 * F8 `IpdTimelineRow`), which are controlled (`expanded` / `onToggle` props).
 *
 * WHERE THE EXPANSION STATE LIVES (F-WIRE decision): here, per row — NOT
 * lifted to `PatientTimeline`. This is an audit surface, not an accordion:
 * an admin comparing two visits (or a visit against the admission that
 * followed it) must be able to hold several rows open at once, so opening
 * one row never collapses another. Per-row state is also the only place the
 * state can sit without threading a setter through `PatientTimelineYearGroup`
 * — nothing above this component reads or derives anything from "which rows
 * are open" (the print module deliberately prints collapsed summaries), so
 * lifting would buy nothing.
 *
 * ROW SLOT / PROP INTERFACE (frozen by F7 / F8, bound verbatim here):
 *
 *   `ref.kind === 'VISIT'`     → `OpdTimelineRow` receives
 *       { visit: row.item, sections, expanded, onToggle }
 *     and renders INSIDE this row's body slot (it renders body content only,
 *     and returns null while collapsed). `sections` is the FULL clinical-record
 *     map so it can read `sections.prescriptions` / `.procedures` / `.labs`
 *     and filter by `visit_id` itself — already in memory, so expanding
 *     issues ZERO further network requests.
 *
 *   `ref.kind === 'ADMISSION'` → `IpdTimelineRow` receives
 *       { admission: row.item, documents: sections.documents, labs: sections.labs, expanded, onToggle }
 *     and REPLACES this row entirely: F8 renders its own header, chevron and
 *     body (including the `timeline-row-ADMISSION-*` container). Nesting it
 *     inside this component's chrome would emit a duplicate row testid and a
 *     doubled border — so the admission branch returns it directly. `labs`
 *     (bk-1128) is ADDITIVE alongside `documents` — the same "hand the row
 *     its own already-fetched slice" shape, no new request.
 *
 * A `row` with no resolved `item` (an unresolvable `ref.section`, R1-4, or a
 * `TimelineRef.kind` this component doesn't specially resolve — LAB_ORDER /
 * PLAN / SITTING / PRESCRIPTION) still renders from the ref alone ("—" where
 * detail would be), never a crash. It renders as a NON-expandable header:
 * there is no detail behind it, and a toggle that opens an empty panel would
 * be a lie.
 */
export default function TimelineRow({ row, sections }) {
  const { ref, bucket, item } = row;
  const isAdmission = ref.kind === TIMELINE_KINDS.ADMISSION;
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((v) => !v);

  if (isAdmission && item) {
    return (
      <IpdTimelineRow
        admission={item}
        documents={sections?.documents}
        labs={sections?.labs}
        expanded={expanded}
        onToggle={toggle}
      />
    );
  }

  const dateLabel = isAdmission
    ? `${formatDayIST(ref.day_ist)} → ${item?.ends_at ? formatDateIST(item.ends_at) : 'Ongoing'}`
    : formatDayIST(ref.day_ist);

  const subLine = isAdmission
    ? [item?.ward_name, item?.bed_label].filter(Boolean).join(' · ') || '—'
    : (item?.doctor_name ? `Dr. ${item.doctor_name}` : '—');

  const statusLabel = isAdmission
    ? (ADMISSION_STATUS_LABELS[item?.status] || item?.status || '—')
    : (item?.status || '—');

  const rowKey = `${ref.kind}-${ref.id}`;
  const expandable = !isAdmission && Boolean(item);

  const header = (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TYPE_CHIP_CLASS[bucket]}`}>
          {bucket}
        </span>
        <span className="text-xs font-bold text-slate-800 dark:text-slate-100">{dateLabel}</span>
        <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 whitespace-nowrap">
          {statusLabel}
        </span>
      </div>
      <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{subLine}</span>
    </div>
  );

  return (
    <div
      data-testid={`timeline-row-${rowKey}`}
      className={`rounded-md border border-slate-200 dark:border-slate-700 border-l-[3px] ${RAIL_CLASS[bucket]} bg-white dark:bg-slate-800 overflow-hidden`}
    >
      {expandable ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          data-testid={`timeline-row-toggle-${rowKey}`}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/60"
        >
          {header}
          <span className={`text-[10px] text-slate-400 transition-transform flex-none ${expanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
      ) : (
        <div className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left">{header}</div>
      )}
      {expandable && expanded && (
        <div
          data-testid={`timeline-row-body-${rowKey}`}
          className="px-3 pb-3 pt-1 bg-slate-50 dark:bg-slate-900/40"
        >
          <OpdTimelineRow visit={item} sections={sections} expanded={expanded} onToggle={toggle} />
        </div>
      )}
    </div>
  );
}
